import { useEffect, useCallback, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { authService } from '../services/authService';
import { sessionService } from '../services/sessionService';
import { chatService } from '../services/chatService';
import { calculatorService } from '../services/calculatorService';
import { AuthUser } from '../services/authService';
import { SessionData } from '../services/sessionService';
import { ChatMessageData } from '../services/chatService';
import { CalculatorData } from '../services/calculatorService';

export const useFirebase = () => {
  const { state, dispatch } = useApp();
  const unsubscribeRefs = useRef<(() => void)[]>([]);

  // Initialize Firebase authentication
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged((user: AuthUser | null) => {
      if (user) {
        dispatch({
          type: 'SET_CURRENT_USER',
          payload: {
            id: user.id,
            name: user.name,
            color: user.color,
            avatar: user.avatar,
            isAnonymous: user.isAnonymous
          }
        });
      } else {
        // User signed out
        dispatch({ type: 'LEAVE_SESSION' });
      }
    });

    return unsubscribe;
  }, [dispatch]);

  // Handle session changes
  const handleSessionChange = useCallback((session: SessionData | null) => {
    if (session) {
      // Only update if this is a different session or if we don't have a current session
      if (!state.currentSession || state.currentSession.id !== session.id) {
        console.log('🔄 useFirebase: Updating session from Firebase:', session.id);
        dispatch({
          type: 'CREATE_SESSION',
          payload: {
            id: session.id,
            name: session.name,
            createdBy: session.createdBy,
            createdAt: session.createdAt.toDate(),
            isActive: session.isActive,
            maxUsers: session.maxUsers,
            settings: session.settings
          }
        });
      } else {
        // If it's the same session, only update if the session is marked as inactive
        // This prevents settings updates from causing issues
        if (!session.isActive && state.currentSession.isActive) {
          console.log('🔄 useFirebase: Session became inactive, leaving session');
          dispatch({ type: 'LEAVE_SESSION' });
        } else {
          console.log('🔄 useFirebase: Session already current, skipping update');
        }
      }
    } else {
      console.log('🔄 useFirebase: Session deleted, leaving session');
      dispatch({ type: 'LEAVE_SESSION' });
    }
  }, [dispatch, state.currentSession]);

  // Handle session users changes
  const handleSessionUsersChange = useCallback((users: any[]) => {
    // Filter out invalid users and remove duplicates
    const validUsers = users
      .filter(user => user && user.id && user.name)
      .map(user => ({
        id: user.id,
        name: user.name,
        color: user.color || '#3b82f6',
        avatar: user.avatar || '👤',
        isOnline: user.isOnline || false,
        isTyping: user.isTyping || false,
        isAnonymous: user.isAnonymous || false
      }))
      .filter((user, index, self) => 
        index === self.findIndex(u => u.id === user.id)
      );
    
    dispatch({
      type: 'SET_SESSION_USERS',
      payload: validUsers
    });
  }, [dispatch]);

  // Handle chat messages changes
  const handleChatMessagesChange = useCallback((messages: ChatMessageData[]) => {
    try {
      // Filter out any invalid messages and remove duplicates
      const validMessages = messages
        .filter(message => message && message.id && message.content)
        .map(message => ({
          id: message.id,
          userId: message.userId,
          userName: message.userName,
          userColor: message.userColor,
          userAvatar: message.userAvatar,
          content: message.content,
          messageType: message.messageType,
          timestamp: message.timestamp instanceof Date ? message.timestamp : (message.timestamp as any).toDate()
        }))
        .filter((message, index, self) => 
          index === self.findIndex(m => m.id === message.id)
        );
      
      dispatch({
        type: 'SET_CHAT_MESSAGES',
        payload: validMessages
      });
    } catch (error) {
      console.error('Error handling chat messages:', error);
    }
  }, [dispatch]);

  // Handle calculator state changes
  const handleCalculatorStateChange = useCallback((calculatorState: CalculatorData | null) => {
    if (calculatorState) {
      dispatch({
        type: 'SET_CURRENT_EXPRESSION',
        payload: calculatorState.currentExpression
      });
      
      dispatch({
        type: 'SET_CURRENT_RESULT',
        payload: calculatorState.currentResult
      });
      
      dispatch({
        type: 'SET_MODE',
        payload: calculatorState.mode
      });
      
      if (calculatorState.isError) {
        dispatch({
          type: 'SET_ERROR',
          payload: {
            isError: true,
            message: calculatorState.errorMessage
          }
        });
      }
      
      dispatch({
        type: 'SET_LOCK',
        payload: {
          isLocked: calculatorState.isLocked,
          lockedBy: calculatorState.lockedBy
        }
      });
    }
  }, [dispatch]);

  // Join session with Firebase integration
  const joinSession = useCallback(async (sessionId: string) => {
    try {
      if (!state.currentUser.id) {
        throw new Error('User not authenticated');
      }

      // Join session
      const session = await sessionService.joinSession(sessionId, state.currentUser.id);
      
      // Set up real-time listeners
      const sessionUnsubscribe = sessionService.onSessionChange(sessionId, handleSessionChange);
      const usersUnsubscribe = sessionService.onSessionUsersChange(sessionId, handleSessionUsersChange);
      const messagesUnsubscribe = chatService.onNewMessages(sessionId, handleChatMessagesChange);
      const calculatorUnsubscribe = calculatorService.onCalculatorStateChange(sessionId, handleCalculatorStateChange);
      
      // Store unsubscribe functions
      unsubscribeRefs.current = [
        sessionUnsubscribe,
        usersUnsubscribe,
        messagesUnsubscribe,
        calculatorUnsubscribe
      ];
      
      // Initialize calculator state if it doesn't exist
      const calculatorState = await calculatorService.getCalculatorState(sessionId);
      if (!calculatorState) {
        await calculatorService.initializeCalculatorState(sessionId, state.currentUser.id);
      }
      
      return session;
    } catch (error) {
      console.error('Failed to join session:', error);
      throw error;
    }
  }, [state.currentUser.id, handleSessionChange, handleSessionUsersChange, handleChatMessagesChange, handleCalculatorStateChange]);

  // Create session with Firebase integration
  const createSession = useCallback(async (name: string, maxUsers: number, settings: any) => {
    try {
      if (!state.currentUser.id) {
        throw new Error('User not authenticated');
      }

      // Create session
      const sessionId = await sessionService.createSession(
        name,
        state.currentUser.id,
        maxUsers,
        settings
      );
      
      // Join the created session
      return await joinSession(sessionId);
    } catch (error) {
      console.error('Failed to create session:', error);
      throw error;
    }
  }, [state.currentUser.id, joinSession]);

  // Leave session and cleanup
  const leaveSession = useCallback(async () => {
    try {
      if (state.currentSession && state.currentUser.id) {
        await sessionService.leaveSession(state.currentSession.id, state.currentUser.id);
      }
      
      // Cleanup all listeners
      unsubscribeRefs.current.forEach(unsubscribe => unsubscribe());
      unsubscribeRefs.current = [];
      
      dispatch({ type: 'LEAVE_SESSION' });
    } catch (error) {
      console.error('Failed to leave session:', error);
    }
  }, [state.currentSession, state.currentUser.id, dispatch]);

  // Send chat message
  const sendChatMessage = useCallback(async (content: string, messageType: 'text' | 'calculation' | 'variable' | 'system' = 'text') => {
    try {
      if (!state.currentSession || !state.currentUser.id) {
        throw new Error('Not in a session');
      }

      await chatService.sendMessage(
        state.currentSession.id,
        state.currentUser.id,
        state.currentUser.name,
        state.currentUser.color,
        state.currentUser.avatar,
        content,
        messageType
      );
    } catch (error) {
      console.error('Failed to send chat message:', error);
      throw error;
    }
  }, [state.currentSession, state.currentUser]);

  // Update calculator state
  const updateCalculatorState = useCallback(async (updates: any) => {
    try {
      if (!state.currentSession || !state.currentUser.id) {
        throw new Error('Not in a session');
      }

      await calculatorService.updateCalculatorState(
        state.currentSession.id,
        state.currentUser.id,
        updates
      );
    } catch (error) {
      console.error('Failed to update calculator state:', error);
      throw error;
    }
  }, [state.currentSession, state.currentUser.id]);

  // Add calculation to history
  const addCalculation = useCallback(async (calculation: any) => {
    try {
      if (!state.currentSession) {
        throw new Error('Not in a session');
      }

      await calculatorService.addCalculation(state.currentSession.id, calculation);
    } catch (error) {
      console.error('Failed to add calculation:', error);
      throw error;
    }
  }, [state.currentSession]);

  // Add variable
  const addVariable = useCallback(async (variable: any) => {
    try {
      if (!state.currentSession) {
        throw new Error('Not in a session');
      }

      await calculatorService.addVariable(state.currentSession.id, variable);
    } catch (error) {
      console.error('Failed to add variable:', error);
      throw error;
    }
  }, [state.currentSession]);

  // Update cursor position
  const updateCursorPosition = useCallback(async (position: number) => {
    try {
      if (!state.currentSession || !state.currentUser.id) {
        return;
      }

      await calculatorService.updateCursorPosition(
        state.currentSession.id,
        state.currentUser.id,
        position
      );
    } catch (error) {
      console.error('Failed to update cursor position:', error);
    }
  }, [state.currentSession, state.currentUser.id]);

  // Update typing status
  const updateTypingStatus = useCallback(async (isTyping: boolean) => {
    try {
      if (!state.currentSession || !state.currentUser.id) {
        return;
      }

      await calculatorService.updateTypingStatus(
        state.currentSession.id,
        state.currentUser.id,
        isTyping
      );
    } catch (error) {
      console.error('Failed to update typing status:', error);
    }
  }, [state.currentSession, state.currentUser.id]);

  // Handle calculations changes
  const handleCalculationsChange = useCallback((calculations: any[]) => {
    try {
      // Filter out any invalid calculations and remove duplicates
      const validCalculations = calculations
        .filter(calc => calc && calc.id && calc.expression && calc.result)
        .map(calc => ({
          id: calc.id,
          expression: calc.expression,
          result: calc.result,
          timestamp: calc.timestamp instanceof Date ? calc.timestamp : (calc.timestamp as any).toDate(),
          userId: calc.userId,
          userName: calc.userName,
          userColor: calc.userColor,
          userAvatar: calc.userAvatar,
          isApproved: calc.isApproved || false,
          approvedBy: calc.approvedBy || [],
          mode: calc.mode || 'basic'
        }))
        .filter((calc, index, self) => 
          index === self.findIndex(c => c.id === calc.id)
        );

      dispatch({
        type: 'SET_CALCULATIONS',
        payload: validCalculations
      });
    } catch (error) {
      console.error('Error handling calculations:', error);
    }
  }, [dispatch]);

  // Handle variables changes
  const handleVariablesChange = useCallback((variables: any[]) => {
    try {
      // Filter out any invalid variables and remove duplicates
      const validVariables = variables
        .filter(variable => variable && variable.id && variable.name && variable.value)
        .map(variable => ({
          id: variable.id,
          name: variable.name,
          value: variable.value,
          timestamp: variable.timestamp instanceof Date ? variable.timestamp : (variable.timestamp as any).toDate(),
          userId: variable.userId,
          userName: variable.userName,
          userColor: variable.userColor,
          userAvatar: variable.userAvatar
        }))
        .filter((variable, index, self) => 
          index === self.findIndex(v => v.id === variable.id)
        );

      dispatch({
        type: 'SET_VARIABLES',
        payload: validVariables
      });
    } catch (error) {
      console.error('Error handling variables:', error);
    }
  }, [dispatch]);

  // Set up Firebase listeners when session changes
  useEffect(() => {
    if (state.currentSession?.id && state.currentUser.id) {
      // Clean up existing listeners
      unsubscribeRefs.current.forEach(unsubscribe => unsubscribe());
      unsubscribeRefs.current = [];

      // Set up new listeners for the current session
      
      const sessionUnsubscribe = sessionService.listenForSessionChanges(
        state.currentSession.id,
        handleSessionChange
      );
      
      const usersUnsubscribe = sessionService.listenForSessionUsers(
        state.currentSession.id,
        handleSessionUsersChange
      );
      
      const messagesUnsubscribe = chatService.listenForChatMessages(
        state.currentSession.id,
        handleChatMessagesChange
      );
      
      const calculatorUnsubscribe = calculatorService.listenForCalculatorState(
        state.currentSession.id,
        handleCalculatorStateChange
      );
      
      const calculationsUnsubscribe = calculatorService.listenForCalculations(
        state.currentSession.id,
        handleCalculationsChange
      );

      const variablesUnsubscribe = calculatorService.listenForVariables(
        state.currentSession.id,
        handleVariablesChange
      );

      unsubscribeRefs.current = [
        sessionUnsubscribe,
        usersUnsubscribe,
        messagesUnsubscribe,
        calculatorUnsubscribe,
        calculationsUnsubscribe,
        variablesUnsubscribe
      ];
    } else {
      // Clean up listeners when no session
      unsubscribeRefs.current.forEach(unsubscribe => unsubscribe());
      unsubscribeRefs.current = [];
    }
  }, [state.currentSession?.id, state.currentUser.id, handleSessionChange, handleSessionUsersChange, handleChatMessagesChange, handleCalculatorStateChange, handleCalculationsChange, handleVariablesChange]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unsubscribeRefs.current.forEach(unsubscribe => unsubscribe());
    };
  }, []);

  return {
    // Authentication
    signUp: authService.signUp.bind(authService),
    signIn: authService.signIn.bind(authService),
    signInWithGoogle: authService.signInWithGoogle.bind(authService),
    signInAnonymously: authService.signInAnonymously.bind(authService),
    signOut: authService.signOut.bind(authService),
    resetPassword: authService.resetPassword.bind(authService),
    updateUserProfile: authService.updateUserProfile.bind(authService),
    
    // Session management
    createSession,
    joinSession,
    leaveSession,
    getActiveSessions: sessionService.getActiveSessions.bind(sessionService),
    
    // Chat
    sendChatMessage,
    
    // Calculator
    updateCalculatorState,
    addCalculation,
    addVariable,
    updateCursorPosition,
    updateTypingStatus,
    
    // Status
    isAuthenticated: authService.isAuthenticated(),
    isAnonymous: authService.isAnonymous(),
    currentUser: authService.getCurrentUser()
  };
};
